import React, { useContext, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { PageContext } from '../../../../contexts/ContextCreator';
import { RESPONSIVE_DESKTOP } from '../../../../contexts/Includes/pageContextHelpers';
import { componentTypes } from '../../../Includes/componentContentTypes';

import './RightRailContent.scss';

const UpcomingEvents = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../../Events/UpcomingEvents/UpcomingEvents'
    )
);
UpcomingEvents.displayName = 'UpcomingEvents';

const RecommendedGroupsList = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../../Groups/RecommendedGroupsList/RecommendedGroupsList'
    )
);
RecommendedGroupsList.displayName = 'RecommendedGroupsList';

const JobSearchFilter = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../../JobV3/JobSearchFilter/JobSearchFilter'
    )
);
JobSearchFilter.displayName = 'JobSearchFilter';

const Recommendations = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../../Messages/Recommendations/Recommendations'
    )
);
Recommendations.displayName = 'Recommendations';

const EmployerReviewsBox = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../../RightRail/EmployerReviewsBox/EmployerReviewsBox'
    )
);
EmployerReviewsBox.displayName = 'EmployerReviewsBox';

const JobsBox = loadable(() =>
    import(/* webpackPrefetch: true */ '../../../RightRail/JobsBox/JobsBox')
);
JobsBox.displayName = 'JobsBox';

const SponsoredCompanyBox = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../../RightRail/SponsoredCompanyBox/SponsoredCompanyBox'
    )
);
SponsoredCompanyBox.displayName = 'SponsoredCompanyBox';

const TrendingContentBox = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../../RightRail/TrendingContentBox/TrendingContentBox'
    )
);
TrendingContentBox.displayName = 'TrendingContentBox';

const CommonCardShell = loadable(() =>
    import(/* webpackPrefetch: true */ '../../CommonCardShell/CommonCardShell')
);
CommonCardShell.displayName = 'CommonCardShell';

const RightRailContent = ({ contents, children }) => {
    const context = useContext(PageContext);

    const renderContents = useMemo(() => {
        if (!contents.length) return [];

        return contents.reduce((aggregator, contentKey) => {
            if (contentKey === componentTypes.SUGGESTED_JOBS)
                return [
                    ...aggregator,
                    <JobsBox
                        suggestedJobs={context.userProps.job}
                        openModal={() => context.modalTrigger('FollowedJobs')}
                    />,
                ];

            if (contentKey === componentTypes.COMPANY_REVIEWS)
                return [
                    ...aggregator,
                    <EmployerReviewsBox
                        reviews={
                            context.userProps.companyReview &&
                            context.userProps.companyReview.reviews
                                ? context.userProps.companyReview.reviews
                                : null
                        }
                        hasFollowingCompanies={
                            (context.userProps.companyReview &&
                                context.userProps.companyReview
                                    .hasFollowingCompanies) ||
                            false
                        }
                        toggleJobReviewSurvey={context.toggleJobReviewSurvey}
                        pageProps={context.pageProps}
                        wrapAuth={context.wrapAuth}
                        isLoggedIn={context.isLoggedIn}
                        modalTrigger={context.modalTrigger}
                    />,
                ];

            if (contentKey === componentTypes.TRENDING_ARTICLES)
                return [
                    ...aggregator,
                    <TrendingContentBox
                        trendingArticles={context.userProps.article}
                    />,
                ];

            if (contentKey === componentTypes.JOB_FILTERS)
                return [
                    ...aggregator,
                    <CommonCardShell>
                        <JobSearchFilter />
                    </CommonCardShell>,
                ];

            if (context.responsive.breakpoint === RESPONSIVE_DESKTOP) {
                if (contentKey === componentTypes.SPONSORED_COMPANY)
                    return [
                        ...aggregator,
                        <SponsoredCompanyBox
                            sponsoredCompany={
                                context.userProps.sponsoredCompany
                            }
                        />,
                    ];

                if (contentKey === componentTypes.UPCOMING_EVENTS)
                    return [
                        ...aggregator,
                        <CommonCardShell>
                            <UpcomingEvents events={context.userProps.event} />
                        </CommonCardShell>,
                    ];

                if (contentKey === componentTypes.RECOMMENDED_CONNECTIONS)
                    return [...aggregator, <Recommendations />];

                if (contentKey === componentTypes.RECOMMENDED_GROUPS)
                    return [
                        ...aggregator,
                        <CommonCardShell>
                            <RecommendedGroupsList />
                        </CommonCardShell>,
                    ];
            }

            return aggregator;
        }, []);
    }, [contents, context]);

    return (
        <div className="component-Layout-RightRailGridLayout-RightRailContent">
            {renderContents.map((component, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={i}>{component}</Fragment>
            ))}
            {children}
        </div>
    );
};

export default RightRailContent;

RightRailContent.propTypes = {
    contents: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.node,
};

RightRailContent.defaultProps = {
    contents: [],
    children: null,
};
